// Persistent local storage with interchangeable drivers: IndexedDB (web/PWA) or Capacitor Filesystem (iOS/Android).
const STORES = ["meta", "cache", "records", "outbox", "photos"];

function idbDriver() {
  let dbp = null;
  const open = () => {
    dbp = dbp || new Promise((resolve, reject) => {
      const req = indexedDB.open("safestart-offline", 1);
      req.onupgradeneeded = () => STORES.forEach((s) => req.result.objectStoreNames.contains(s) || req.result.createObjectStore(s));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbp;
  };
  const tx = async (store, mode, fn) => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const t = db.transaction(store, mode);
      const r = fn(t.objectStore(store));
      t.oncomplete = () => resolve(r?.result);
      t.onerror = () => reject(t.error);
    });
  };
  return {
    get: (s, k) => tx(s, "readonly", (o) => o.get(k)),
    put: (s, k, v) => tx(s, "readwrite", (o) => o.put(v, k)),
    del: (s, k) => tx(s, "readwrite", (o) => o.delete(k)),
    clear: (s) => tx(s, "readwrite", (o) => o.clear()),
    all: async (s) => {
      const db = await open();
      return new Promise((resolve, reject) => {
        const out = [];
        const req = db.transaction(s, "readonly").objectStore(s).openCursor();
        req.onsuccess = () => {
          const c = req.result;
          if (!c) return resolve(out);
          out.push({ key: c.key, value: c.value });
          return c.continue();
        };
        req.onerror = () => reject(req.error);
      });
    },
  };
}

const toB64 = (blob) => new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(blob); });
const fromB64 = async (url) => (await fetch(url)).blob();

// App-private Data directory (sandboxed, OS data protection). Blobs are serialised as data URLs.
function capacitorDriver(cap) {
  const fs = cap.Plugins.Filesystem;
  const opts = { directory: "DATA", encoding: "utf8" };
  const path = (s, k) => `safestart/${s}/${encodeURIComponent(String(k))}.json`;
  const encode = async (v) => (v && v.blob instanceof Blob ? { ...v, blob: await toB64(v.blob), __b64: true } : v);
  const decode = async (v) => (v && v.__b64 ? { ...v, blob: await fromB64(v.blob) } : v);
  const read = async (p) => { try { return await decode(JSON.parse((await fs.readFile({ path: p, ...opts })).data)); } catch { return undefined; } };
  return {
    get: (s, k) => read(path(s, k)),
    put: async (s, k, v) => fs.writeFile({ path: path(s, k), data: JSON.stringify(await encode(v)), recursive: true, ...opts }),
    del: (s, k) => fs.deleteFile({ path: path(s, k), directory: "DATA" }).catch(() => {}),
    clear: (s) => fs.rmdir({ path: `safestart/${s}`, directory: "DATA", recursive: true }).catch(() => {}),
    all: async (s) => {
      let files = [];
      try { files = (await fs.readdir({ path: `safestart/${s}`, directory: "DATA" })).files; } catch { return []; }
      const out = [];
      for (const f of files) {
        const name = typeof f === "string" ? f : f.name;
        const key = decodeURIComponent(name.replace(/\.json$/, ""));
        out.push({ key: s === "outbox" ? Number(key) : key, value: await read(`safestart/${s}/${name}`) });
      }
      return out.sort((a, b) => (a.key > b.key ? 1 : -1));
    },
  };
}

export const isNative = () => Boolean(window.Capacitor?.isNativePlatform?.());
export const storage = isNative() ? capacitorDriver(window.Capacitor) : idbDriver();
